/* eslint prefer-promise-reject-errors: 0 */
/* eslint no-nested-ternary: 0 */
import axios from 'axios';
import { Transport, ESPLoader } from 'esptool-js';
import { DEFAULT_PORT_SOURCE, getAgentPorts, getNewMessage, getNewProgress, getNewState, getPorts } from '../actions/bloquesAction';
import { getBuildTarget } from './buildTargets';
import { uniquePortName, usbIdToHex } from './portIdentity';
import { REASON, matchStagedRead, observePlugEvents, requestBeforeLink, requestSerialNumber, resolveBeforeLink, resolveFromGrants, resolveOnPlug } from './usbSerialNumber';
import { getSerialSession } from './traceability/serialSession';
import MonitorFeed from './traceability/monitorFeed';
import { createFirmwareNoiseFilter } from './traceability/firmwareNoise';

// Quién pide el puerto compartido cuando el monitor serie está abierto. La sesión
// lleva la cuenta por dueño, así que el ping de trazabilidad y el monitor pueden
// tenerlo tomado a la vez y el puerto se cierra recién cuando se van los dos.
const MONITOR_OWNER = 'serial-monitor';

/**
 * How long serial number reads are held off once an upload starts. Flashing resets
 * the board, and an ESP with native usb re-enumerates while doing it, so pairing a
 * plug in that window could tie the wrong arrival to this port.
 *
 * A deadline and not a flag: a flash that never reports back would leave a flag
 * set and switch serial reads off for the rest of the session.
 */
const FLASH_QUIET_MS = 30000;

/**
 * Velocidades de escritura, de la más rápida a la más segura. La negociación
 * inicial con la ROM va siempre a ROM_BAUDRATE: esa parte tiene que ser confiable,
 * y esptool sube la velocidad recién después de cargar el stub.
 *
 * Cada reintento de conexión baja un escalón. Los CH340 —sobre todo los clones—
 * no siempre bancan 921600, y no avisan cuando fallan: change_baud no tira error,
 * deja el enlace desincronizado y recién revienta a mitad de la escritura. Por eso
 * cada velocidad se comprueba con flash_id antes de darla por buena.
 */
const ROM_BAUDRATE = 115200;
const FLASH_BAUDRATES = [921600, 460800, ROM_BAUDRATE];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const resetTransport = async (transport) => {
  try {
    if (transport.device?.readable || transport.device?.writable) {
      await transport.device.setSignals({
        dataTerminalReady: false,
        requestToSend: true,
      });
      await transport.device.setSignals({
        dataTerminalReady: false,
        requestToSend: false,
      });
      await transport.disconnect();
    }
  } catch (err) {
    console.log('resetTransport error:', err);
  }
};

/**
 * Cuánto se retiene el evento de conexión de una placa esperando a que se sepa
 * su número.
 *
 * Largo porque el camino más lento es legítimo y tiene a una persona adentro:
 * instalar el firmware es compilar, grabar y reiniciar, y antes de eso el usuario
 * tiene que leer un modal y apretar un botón. Cortar en el medio de eso reportaría
 * sin número a una placa que estaba por tenerlo.
 *
 * Y existe porque nadie puede quedar esperando para siempre: si el que tenía que
 * contestar no está -- la pantalla se desmontó, algo se colgó --, la placa se
 * reporta igual.
 */
const IDENTITY_HOLD_MS = 120000;

class WebESPAgent {
  constructor(store, board, socketElectronConnection, httpElectronConnection, serialMonitorMessages, intl, portSource = DEFAULT_PORT_SOURCE, { sharedPort = false, identity = false } = {}) {
    this.store = store;
    // This instance only sees the ports of its own transport, so it publishes them
    // under its own source and the reducer merges them with the other agents'.
    this.portSource = portSource;
    this.board = board;
    this.socketElectronConnection = socketElectronConnection;
    this.httpElectronConnection = httpElectronConnection;
    this.selectedPort = null;
    this.monitor = '';
    this.portList = [];
    this.serialPortList = [];
    this.intl = intl;
    this.serialMonitorMessages = serialMonitorMessages;
    this.monitorReader = null;
    this.monitorPort = null;
    /**
     * Con esto en true el puerto lo maneja serialSession y el monitor es un
     * suscriptor más, en lugar de abrirlo para él solo.
     *
     * Va apagado por defecto a propósito: solo BlocklyPanel pide la trazabilidad,
     * y las demás pantallas que usan este agente siguen con el camino de siempre.
     */
    this.sharedPort = sharedPort;
    /**
     * Con esto en true la identidad de cada placa la resuelve BoardIdentity sobre
     * el puerto ya vinculado, y el evento de conexión espera a que conteste.
     *
     * Apagado por defecto: sin alguien que la resuelva, prender esto retendría
     * cada placa hasta que venza el plazo.
     */
    this.identity = identity;
    // Puerto -> el plazo que lo suelta si la identidad no llega. Van aparte de la
    // entrada porque esa lista termina en redux.
    this.identityHolds = new Map();
    this.monitorSession = null;
    this.monitorSubscription = null;
    this.monitorFeed = null;
    // The port the user picked in the chooser last. The instance is the anchor:
    // WebSerial hands back the same SerialPort object for the same physical port.
    this.lastLinkedPort = null;
    /**
     * What the serial number read came back with, waiting for the port it belongs
     * to. The read runs before the board is linked, so it lands before there is
     * anything to write it on, and the port chooser is narrowed with the ids of
     * whatever it identified.
     *
     * Only an explicit link consumes it. A board arriving on its own, or a fresh
     * enumeration, must not pick up a read the user made pointing at another one.
     */
    this.stagedRead = null;
    /**
     * Borra la flash entera antes de escribir. Apagado por defecto: para subir un
     * programa es tiempo perdido (ver runFlash). Se enciende desde afuera, por ahora
     * solo a mano, para rescatar una placa que quedó con estado viejo incompatible
     * —NVS, EEPROM o filesystem— de un proyecto anterior.
     */
    this.deepErase = false;

    if ('serial' in navigator) {
      navigator.serial.addEventListener('connect', (event) => {
        this.addPort(event.target, { fromPlug: true });
      });
      navigator.serial.addEventListener('disconnect', (event) => {
        this.removePort(event.target);
      });
      this.store.dispatch(getNewState('#### PORT CONNECTED'));
      this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
      // Started before any board arrives: pairing a plug needs both of its events,
      // and the usb one is missed if nothing was listening when it fired.
      observePlugEvents();
    }

    this.findInitialPorts();
  }

  disconnectSocket = () => {}

  stopCheckingAgent = () => {}

  /**
   * The names the selector shows. Sent as soon as a board is listed, so it can be
   * picked right away, while the traceability list waits for its serial number.
   *
   * Derived from serialPortList instead of being kept in sync by index: both arrays
   * used to be spliced with the same index, which only holds while every push
   * happens in the same tick.
   */
  publishPortNames = ({ announceState = true } = {}) => {
    this.portList = this.serialPortList.map((p) => p.Name);
    this.store.dispatch(getPorts(this.portList, this.portSource));
    if (!announceState) return;
    if (this.portList.length > 0) {
      this.store.dispatch(getNewState('#### BOARD CONNECTED'));
    } else {
      this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
    }
  }

  /**
   * The full list, which is what the connection events are diffed from. Sent once
   * the serial number of a new board has been read or given up on, so the board is
   * reported a single time with its identity already resolved.
   */
  publishPorts = ({ announceState = true } = {}) => {
    this.publishPortNames({ announceState });
    this.store.dispatch(getAgentPorts(this.serialPortList, this.portSource));
  }

  findEntryByPort = (port) => (port ? this.serialPortList.find((p) => p.SerialPort === port) : null) || null

  // Listed ports of one model, which is what tells a read resolved without asking
  // apart from one that cannot be tied to a single board.
  countPortsWithIds = (vendorID, productID) => this.serialPortList.filter((p) => p.VendorID === vendorID && p.ProductID === productID).length

  /**
   * Ties the staged read to a port, and clears it either way: one that missed its
   * port is spent, and leaving it there would hand it to the next board instead.
   */
  consumeStagedRead = (vendorID, productID) => {
    const staged = this.stagedRead;
    this.stagedRead = null;
    if (!staged) return null;
    return matchStagedRead(staged, { vendorID, productID, sameIdPortCount: this.countPortsWithIds(vendorID, productID) });
  }

  /**
   * Writes the staged read on a board that was already listed when the read was
   * made, which is the case when the page opened with it plugged in. Its CONNECTED
   * went out before the serial could be read, so this one travels as a correction
   * of that event rather than as part of it.
   */
  applyStagedRead = (port) => {
    const entry = this.findEntryByPort(port);
    if (!entry) return;
    const result = this.consumeStagedRead(entry.VendorID, entry.ProductID);
    if (result) this.applySerialNumber(port, result);
  }

  /**
   * Places the staged read on a board that is already listed, when the read could
   * not be aimed at one to begin with: more than one port is listed and none was
   * linked in this session, so there was no entry to read against. The ids of the
   * device are what place it, and only a single listed port carrying them is
   * placed on - two of the same model cannot be told apart.
   *
   * A board reached this way had its connection reported before the read, so the
   * serial travels as a correction of that event.
   */
  applyStagedReadToListedPort = () => {
    const staged = this.stagedRead;
    if (!staged || !staged.serialNumber) return;
    const matches = this.serialPortList.filter((p) => matchStagedRead(staged, { vendorID: p.VendorID, productID: p.ProductID }).serialNumber);
    if (matches.length !== 1) return;
    this.stagedRead = null;
    this.applySerialNumber(matches[0].SerialPort, staged);
  }

  /**
   * Narrows the WebSerial chooser to the board the read identified, so the two
   * choosers of the flow cannot be answered with different boards. Undefined when
   * nothing was identified, which leaves the chooser as it was.
   */
  serialRequestOptions = () => {
    const staged = this.stagedRead;
    if (!staged || staged.vendorId == null || staged.productId == null) return undefined;
    return { filters: [{ usbVendorId: staged.vendorId, usbProductId: staged.productId }] };
  }

  // See FLASH_QUIET_MS.
  holdSerialReads = () => { this.serialReadsHeldUntil = Date.now() + FLASH_QUIET_MS; }

  serialReadsHeld = () => Date.now() < (this.serialReadsHeldUntil || 0)

  /**
   * Writes what was read onto the listed port, publishing nothing. An empty read
   * is an ordinary outcome: an older board carries no serial in its descriptor,
   * and one that has never been through the WebUSB chooser cannot be read without
   * it.
   *
   * The port is looked up again rather than held on to, because the read takes
   * long enough for the board to have been unplugged in the meantime, and writing
   * onto an entry already spliced out of the list would go nowhere.
   */
  writeSerialNumber = (port, result) => {
    if (!port || !result) return false;
    const index = this.serialPortList.findIndex((p) => p.SerialPort === port);
    if (index < 0) return false;
    /**
     * Written even when nothing was read, because why a board has no serial is
     * what the event carries as serial_reason, and it is the only thing that tells
     * a browser which faked the read apart from a board that carries none.
     *
     * Never written over the reason of a serial already on the entry: a later
     * empty read would otherwise replace 'none' with the reason of a read that
     * changed nothing.
     */
    if (!this.serialPortList[index].UsbSerialNumber || result.serialNumber) {
      this.serialPortList[index].SerialReason = result.reason;
    }
    if (!result.serialNumber) return false;
    if (this.serialPortList[index].UsbSerialNumber === result.serialNumber) return true;
    this.serialPortList[index].UsbSerialNumber = result.serialNumber;
    this.serialPortList[index].SerialConfidence = result.confidence;
    this.serialPortList[index].SerialMatch = result.match;
    return true;
  }

  /**
   * Writes it and republishes, for a read that landed after the board had already
   * been reported, which is what the modal produces. No connection event comes out
   * of it: the diff keys ports by name, so nothing there changed. The serial
   * travels with the next event of that board instead, which right after the modal
   * is the CODE_BUILD of the upload that opened it.
   */
  applySerialNumber = (port, result) => {
    const written = this.writeSerialNumber(port, result);
    if (written) this.publishPorts({ announceState: false });
    return written;
  }

  removePort = (port) => {
    this.clearIdentityHold(port);
    for (let i = 0; i < this.serialPortList.length; i += 1) {
      if (this.serialPortList[i].SerialPort === port) {
        this.serialPortList.splice(i, 1);
        this.publishPorts();
        break;
      }
    }
  }

  addPort = (port, { fromPlug = false, linked = false } = {}) => {
    let exists = false;
    this.serialPortList.forEach((p) => {
      if (p.SerialPort === port) {
        exists = true;
      }
    });
    if (!exists) {
      const portInfo = port.getInfo();
      const vendorID = usbIdToHex(portInfo.usbVendorId);
      const productID = usbIdToHex(portInfo.usbProductId);
      const name = uniquePortName(`Puerto (${vendorID}-${productID})`, this.serialPortList);

      this.serialPortList.push({
        Name: name,
        SerialPort: port,
        VendorID: vendorID,
        ProductID: productID,
      });
      // The board shows up in the selector now; the connection event waits below.
      this.publishPortNames();

      /**
       * La identidad la resuelve BoardIdentity sobre el puerto ya vinculado, así
       * que acá no se lee nada por WebUSB: se retiene el reporte y se espera.
       */
      if (this.identity) {
        this.holdIdentity(port);
        this.publishPorts({ announceState: false });
        return;
      }

      if (this.serialReadsHeld()) {
        this.publishPorts();
        return;
      }
      // The serial was read before this board was linked, so it goes on the entry
      // before the list is published and the board is reported once, identified.
      if (linked && this.stagedRead) {
        this.writeSerialNumber(port, this.consumeStagedRead(vendorID, productID));
        this.publishPorts({ announceState: false });
        return;
      }
      /**
       * The full list is held back until the read settles, so the board is reported
       * once, already identified, instead of being reported anonymous and corrected
       * afterwards. Both reads always settle - one answers straight away and the
       * other has its own deadline - and the catch covers the rest, because a list
       * never published would be a board never reported.
       */
      const read = fromPlug
        ? resolveOnPlug({ vendorID, productID })
        : resolveFromGrants({ vendorID, productID });
      read
        .then((result) => this.writeSerialNumber(port, result))
        .catch(() => {})
        .then(() => this.publishPorts({ announceState: false }));
    } else if (linked) {
      this.applyStagedRead(port);
    }
  }

  /**
   * Retiene el reporte de una placa hasta que se sepa con qué número se
   * identifica. La placa se lista igual -- aparece en el selector y se le puede
   * mandar código --; lo único que espera es el evento de conexión.
   *
   * Ver pendingIdentity en boardConnectionInfo: el serial es con lo que se agrupa
   * el uso, así que conviene reportar la placa una sola vez y ya identificada.
   */
  holdIdentity = (port) => {
    const entry = this.findEntryByPort(port);
    if (!entry || this.identityHolds.has(port)) return;
    entry.IdentityPending = true;
    this.identityHolds.set(port, setTimeout(() => this.releaseIdentityHold(port), IDENTITY_HOLD_MS));
  }

  clearIdentityHold = (port) => {
    const timer = this.identityHolds.get(port);
    if (timer) clearTimeout(timer);
    this.identityHolds.delete(port);
  }

  /**
   * Se acabó la espera y la identidad no llegó. La placa se reporta igual, sin
   * número: una placa sin identificar en los datos es peor que nada, pero una
   * conexión que nunca se reportó es peor todavía. Si el número aparece después,
   * viaja como corrección de ese mismo evento.
   */
  releaseIdentityHold = (port) => {
    this.clearIdentityHold(port);
    const entry = this.findEntryByPort(port);
    if (!entry || !entry.IdentityPending) return;
    entry.IdentityPending = false;
    this.publishPorts({ announceState: false });
  }

  /**
   * Lo que averiguó la máquina de identidad sobre esta placa. Es lo que suelta el
   * evento de conexión retenido, con el número ya adentro.
   *
   * Se escribe aunque no haya número: el serial de hardware sirve igual, y sin
   * soltar la retención la placa no se reportaría hasta que venza el plazo.
   */
  setBoardIdentity = (port, { serialString = null, hardwareSerial = null, source = null } = {}) => {
    const entry = this.findEntryByPort(port);
    if (!entry) return false;
    this.clearIdentityHold(port);
    if (hardwareSerial) entry.HardwareSerial = hardwareSerial;
    if (serialString) {
      entry.SerialString = serialString;
      entry.SerialSource = source;
      entry.SerialReason = REASON.NONE;
    }
    entry.IdentityPending = false;
    this.publishPorts({ announceState: false });
    return true;
  }

  /**
   * El serial que el chip usb declara en su descriptor, para la máquina de
   * identidad. Es el camino de las pocas placas entregadas con el número grabado
   * en el ch340 y nada en la EEPROM.
   *
   * `prompt` es la diferencia entre pedirle el permiso al usuario -- abre el
   * selector, necesita venir de un click -- y leerlo de los permisos ya dados, que
   * no abre nada. La segunda forma es la que hace que esas placas molesten una
   * sola vez por navegador.
   *
   * Lo que se lee pasa por el mismo chequeo de siempre antes de devolverse: un
   * serial pegado a la placa equivocada corrompe la historia de dos placas a la
   * vez, y eso no lo arregla nadie después.
   */
  readUsbSerialFor = async (port, { prompt = false } = {}) => {
    const entry = this.findEntryByPort(port);
    if (!entry) return null;
    const result = prompt
      ? await requestSerialNumber({ vendorID: entry.VendorID, productID: entry.ProductID })
      : await resolveFromGrants({ vendorID: entry.VendorID, productID: entry.ProductID });
    const matched = matchStagedRead(result, {
      vendorID: entry.VendorID,
      productID: entry.ProductID,
      sameIdPortCount: this.countPortsWithIds(entry.VendorID, entry.ProductID),
    });
    return matched.serialNumber || null;
  }

  /**
   * Reads the serial number of the board about to be linked. Runs from a click,
   * which WebUSB requires to open its chooser, and comes before the link so that
   * the port can be listed with its identity already in hand.
   *
   * Answers with a REASON rather than a plain yes or no, because the step has to
   * tell a dismissed chooser apart from a board whose descriptor has no serial:
   * the first is the one worth offering again, and the second never will be. That
   * is what lets the step be mandatory without walling in an older board.
   *
   * A board that is already listed is read the other way round, against the ids of
   * its port: that happens when the page opened with it plugged in, and when it
   * already carries a serial there is nothing to ask for at all.
   */
  fillBoardSerialNumber = async (port = this.lastLinkedPort, { unfiltered = false } = {}) => {
    const entry = this.findEntryByPort(port) || ((this.serialPortList.length === 1) ? this.serialPortList[0] : null);
    if (entry) {
      if (entry.UsbSerialNumber) return REASON.NONE;
      // Narrowed to the ids of the port itself, which cannot hide the board being
      // read. Lifting that leaves the chooser offering everything, so what comes
      // back goes through the same check as a read taken before linking instead of
      // being written on the strength of the filter alone.
      const result = unfiltered
        ? await requestBeforeLink({ unfiltered: true })
        : await requestSerialNumber({ vendorID: entry.VendorID, productID: entry.ProductID });
      const matched = matchStagedRead(result, {
        vendorID: entry.VendorID,
        productID: entry.ProductID,
        sameIdPortCount: this.countPortsWithIds(entry.VendorID, entry.ProductID),
      });
      this.applySerialNumber(entry.SerialPort, matched);
      // A serial that was read but belongs to something else leaves the step where
      // a dismissed chooser does: nothing was written, and another go is what the
      // user needs. Saying it was read would be a success with nothing behind it.
      if (result.reason === REASON.NONE && !matched.serialNumber) return REASON.UNAVAILABLE;
      return result.reason;
    }
    // Answered from the grants when it can be, so a board goes through the chooser
    // once per browser and is recognised on its own from then on. Asking again
    // after a failed read costs one query and answers the same way, unless the
    // boards on the machine changed in between - in which case the answer is right.
    let result = await resolveBeforeLink();
    if (result.reason === REASON.UNAVAILABLE) result = await requestBeforeLink({ unfiltered });
    this.stagedRead = (result.reason === REASON.UNAVAILABLE) ? null : result;
    this.applyStagedReadToListedPort();
    return result.reason;
  }

  getSerialPortByName = (name = '') => {
    for (let i = 0; i < this.serialPortList.length; i += 1) {
      if (this.serialPortList[i].Name === name) {
        return this.serialPortList[i].SerialPort;
      }
    }
    return null;
  }

  connectNewBoard = () => {
    if ('serial' in navigator) {
      navigator.serial.requestPort(this.serialRequestOptions())
        .then((port) => {
          this.lastLinkedPort = port;
          this.addPort(port, { linked: true });
        })
        .catch(() => {
          if (this.serialPortList.length === 0) {
            this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotSelected' })));
            this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
          }
        });
    }
  }

  findPorts = () => {}

  findInitialPorts = () => {
    if ('serial' in navigator) {
      navigator.serial.getPorts()
        .then((ports) => {
          ports.forEach((port) => this.addPort(port));
        })
        .catch((error) => {
          this.store.dispatch(getNewMessage(error.toString()));
        });
    }
  }

  /**
   * El flasheo necesita el puerto para él solo: esptool lo abre por su cuenta y a
   * otra velocidad. Con el puerto compartido se lo pedimos prestado a la sesión,
   * que lo cierra antes y lo vuelve a abrir después sin que el monitor ni el ping
   * tengan que enterarse.
   */
  flashESP = async (serialPort, fileArray, totalSize) => {
    if (this.sharedPort) {
      const session = getSerialSession(serialPort);
      return session.withExclusiveAccess(() => this.runFlash(serialPort, fileArray, totalSize));
    }
    return this.runFlash(serialPort, fileArray, totalSize);
  }

  runFlash = async (serialPort, fileArray, totalSize) => {
    // Close monitor if it holds a reader lock on this port
    if (!this.sharedPort && this.monitorPort === serialPort && this.monitorReader) {
      await this.closeMonitor();
    }

    // Ensure port is fully closed before esptool opens it
    try {
      await serialPort.close();
    } catch (e) {
      // Port may already be closed — that's fine
    }

    // Give the OS time to release the port
    await sleep(500);

    this.store.dispatch(getNewState('#### UPLOAD TO AGENT'));
    this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 75));

    // Retry connecting — CH340 on Windows sometimes needs multiple attempts.
    // Cada reintento además baja un escalón de velocidad, así que un adaptador que
    // no banca 921600 termina subiendo igual, solo que más lento.
    let transport;
    let esploader;
    const MAX_RETRIES = FLASH_BAUDRATES.length;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
      const baudrate = FLASH_BAUDRATES[attempt - 1];
      transport = new Transport(serialPort);
      esploader = new ESPLoader({
        transport,
        baudrate,
        romBaudrate: ROM_BAUDRATE,
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        await esploader.main_fn();
        try {
          // eslint-disable-next-line no-await-in-loop
          await esploader.flash_id();
        } catch (flashIdErr) {
          // A la velocidad segura no hubo cambio de baudrate que verificar, así que
          // un flash_id que falla ahí sigue sin ser motivo para abortar. Más arriba
          // es la señal de que el enlace quedó mudo: se reintenta un escalón abajo.
          if (baudrate !== ROM_BAUDRATE) throw flashIdErr;
          console.log('flash_id non-fatal error (continuing):', flashIdErr.message);
        }
        break; // success
      } catch (err) {
        console.log(`ESP init attempt ${attempt}/${MAX_RETRIES} a ${baudrate} baudios:`, err.message);
        if (attempt === MAX_RETRIES) {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(
            `Error al inicializar ESP: ${err.message || err}. Verificá que ningún otro programa tenga el puerto abierto.`,
          ));
          // eslint-disable-next-line no-await-in-loop
          await resetTransport(transport);
          return;
        }
        // Clean up before retrying
        // eslint-disable-next-line no-await-in-loop
        await resetTransport(transport);
        // eslint-disable-next-line no-await-in-loop
        await sleep(1500);
      }
    }

    // Chip validation
    const chipName = esploader.chip.CHIP_NAME.toLowerCase().replace('-', '');
    const boardFamily = this.board.compilerBoard.split(':')[1];
    if (!chipName.startsWith(boardFamily)) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(
        `Chip detectado (${esploader.chip.CHIP_NAME}) no coincide con firmware compilado para ${this.board.compilerBoard}`,
      ));
      await resetTransport(transport);
      return;
    }

    // Subir un programa no necesita el borrado total: write_flash borra por su cuenta
    // los sectores que va a escribir, y lo que quede más allá del final de la imagen
    // nueva no lo lee nadie —el bootloader se guía por la cabecera, no por lo que
    // haya en la flash—. El chip erase son ~15 s fijos que además no bajan con la
    // velocidad, porque los hace el chip de flash y no el enlace serie.
    //
    // Sigue haciendo falta cuando la placa arrastra estado de un proyecto anterior:
    // NVS, EEPROM o un filesystem que el código nuevo lee sin escribir, o un layout
    // de particiones que cambió. Eso se pide aparte; todavía no hay forma de hacerlo
    // desde la UI.
    if (this.deepErase) {
      try {
        await esploader.erase_flash();
      } catch (err) {
        this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
        this.store.dispatch(getNewMessage(
          `Error al borrar flash: ${err.message?.includes('The device has been lost') ? 'Se perdió la conexión' : (err.message || err)}`,
        ));
        await resetTransport(transport);
        return;
      }
    }

    let totalWritten = 0;
    try {
      await esploader.write_flash({
        fileArray,
        flashSize: 'keep',
        flashMode: 'keep',
        flashFreq: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (fileIndex, written, total) => {
          const uncompressedWritten = (written / total) * fileArray[fileIndex].data.length;
          const newPct = Math.floor(
            ((totalWritten + uncompressedWritten) / totalSize) * 100,
          );
          if (written === total) {
            totalWritten += uncompressedWritten;
            return;
          }
          // Map 75-99 range for flash progress
          const mappedPct = 75 + Math.floor((newPct / 100) * 24);
          this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', mappedPct));
        },
      });
    } catch (err) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(
        `Error al escribir flash: ${err.message?.includes('The device has been lost') ? 'Se perdió la conexión' : (err.message || err)}`,
      ));
      await resetTransport(transport);
      return;
    }

    await sleep(100);
    await resetTransport(transport);
    this.store.dispatch(getNewState('#### FINISH AGENT'));
  }

  parseEspBuildResponse = (response) => {
    const parts = response.data.payload?.parts || response.data.parts;
    const fileArray = parts.map((part) => ({
      data: atob(part.data),
      address: parseInt(part.address, 16),
    }));
    const totalSize = fileArray.reduce((sum, f) => sum + f.data.length, 0);
    return { fileArray, totalSize };
  }

  uploadToPort = (code = '', board = '', port = '') => {
    this.holdSerialReads();
    const serialPort = this.getSerialPortByName(port);

    this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.codeIsUploading' })));
    this.store.dispatch(getNewState('#### UPLOAD TO PORT'));
    this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 0));

    const { uri: buildURI, params: buildParams } = getBuildTarget(board.compilerBoard, code, this.httpElectronConnection);
    this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));
    this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 25));

    axios.post(
      buildURI,
      buildParams,
      {
        withCredentials: false,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: '*/*',
        },
      },
    ).then(async (response) => {
      const parts = response.data.payload?.parts || response.data.parts;
      if (response.data.status === 'SUCCESS' && parts) {
        this.store.dispatch(getNewState('#### FINISH EB_BUILD'));
        this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 50));

        const { fileArray, totalSize } = this.parseEspBuildResponse(response);

        if (serialPort) {
          await this.flashESP(serialPort, fileArray, totalSize);
        } else {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotConnected' })));
        }
      } else {
        this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
        this.store.dispatch(getNewMessage(
          response.data.message
            ? (typeof response.data.message === 'string' ? response.data.message : JSON.stringify(response.data.message))
            : 'Error de compilación',
        ));
      }
    }).catch((responseError) => {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(responseError.toString()));
    });
  }

  uploadRemoteSession = (hex = '', board = '', port = '') => new Promise((resolve, reject) => {
    this.holdSerialReads();
    const serialPort = this.getSerialPortByName(port);
    this.store.dispatch(getNewState('#### UPLOAD TO PORT'));

    if (!port || port === '') {
      this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotConnected' })));
      reject(this.intl.formatMessage({ id: 'agent.boardNotConnected' }));
      return;
    }
    this.store.dispatch(getNewState('#### BOARD CONNECTED'));
    if (!board) {
      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardMustBeSelected' })));
      reject('Debes seleccionar una placa');
      return;
    }

    this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.remoteCodeIsUploading' })));
    this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));

    // hex for ESP is a JSON string with parts array
    try {
      let parts;
      if (typeof hex === 'string') {
        const parsed = JSON.parse(hex);
        parts = parsed.parts || parsed;
      } else {
        parts = hex.parts || hex;
      }

      const fileArray = parts.map((part) => ({
        data: atob(part.data),
        address: parseInt(part.address, 16),
      }));
      const totalSize = fileArray.reduce((sum, f) => sum + f.data.length, 0);

      this.flashESP(serialPort, fileArray, totalSize)
        .then(() => resolve('OK'))
        .catch((err) => {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(err.toString()));
          reject(err.toString());
        });
    } catch (err) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(`Error procesando firmware: ${err.message || err}`));
      reject(err.toString());
    }
  })

  /**
   * El monitor sobre el puerto compartido.
   *
   * Acá está la diferencia que importa: el puerto puede estar ya abierto por el
   * ping de trazabilidad, y eso no es un error sino el caso normal. En vez de
   * abrirlo, el monitor se anota como interesado y se suscribe al texto que la
   * sesión ya separó de las tramas del protocolo. Si nadie lo tenía abierto, el
   * acquire lo abre igual.
   *
   * Lo que llega al monitor es solo lo que imprime el sketch del usuario: las
   * tramas binarias de nuestros pings las filtró la sesión antes.
   */
  openSharedMonitor = async (serialPort, baudios) => {
    const session = getSerialSession(serialPort, { baudRate: baudios });
    /**
     * El puerto ya abierto conserva su velocidad: el protocolo Educabot corre a
     * 9600 fijos y reabrir a otra velocidad dejaría de entender las tramas. Queda
     * anotado porque el selector de baudios del monitor sigue mostrando lo que
     * eligió el usuario, y en ese caso no es lo que está pasando.
     */
    if (session.isOpen && session.baudRate !== baudios) {
      console.log(`El monitor queda a ${session.baudRate} baudios: el puerto ya estaba abierto a esa velocidad.`); // eslint-disable-line no-console
    }
    this.monitorSession = session;
    this.monitorPort = serialPort;
    if (!this.monitorSubscription) {
      // Amortiguado: ver MonitorFeed. Sin esto, un sketch que imprime sin delay
      // deja la pestaña sin main thread y el monitor no llega ni a abrirse.
      this.monitorFeed = new MonitorFeed((entry) => this.serialMonitorMessages.next(entry));
      /**
       * La sesión ya sacó las tramas binarias, pero la librería de trazabilidad
       * además imprime diagnósticos en texto plano, que por el protocolo son
       * indistinguibles de lo que escribe el usuario. Ver firmwareNoise.
       */
      const dropFirmwareNoise = createFirmwareNoiseFilter();
      this.monitorSubscription = session.text.subscribe((text) => this.monitorFeed.push(dropFirmwareNoise(text)));
    }
    try {
      await session.acquire(MONITOR_OWNER);
    } catch (err) {
      this.store.dispatch(getNewMessage(`Error al abrir monitor: ${err.message || err}`));
    }
  }

  closeSharedMonitor = async () => {
    if (this.monitorSubscription) {
      this.monitorSubscription.unsubscribe();
      this.monitorSubscription = null;
    }
    if (this.monitorFeed) {
      this.monitorFeed.stop();
      this.monitorFeed = null;
    }
    this.monitor = '';
    const session = this.monitorSession;
    this.monitorSession = null;
    this.monitorPort = null;
    if (session) await session.release(MONITOR_OWNER);
  }

  openMonitor = async (port = '', baudios = 115200) => {
    const serialPort = this.getSerialPortByName(port);
    if (!serialPort) return;

    if (this.sharedPort) {
      await this.openSharedMonitor(serialPort, baudios);
      return;
    }

    try {
      if (!serialPort.readable) {
        await serialPort.open({ baudRate: baudios });
      }

      this.monitorPort = serialPort;
      const reader = serialPort.readable.getReader();
      this.monitorReader = reader;
      const decoder = new TextDecoder();

      const processChunk = ({ value, done }) => {
        if (done) return;
        const message = decoder.decode(value);
        this.monitor += message;
        if (message.match(/\n/g) || this.monitor.length > 100) {
          const date = new Date();
          const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`;
          this.serialMonitorMessages.next({ time, message: this.monitor });
          this.monitor = '';
        }
        reader.read().then(processChunk).catch((err) => {
          if (err.name !== 'NetworkError') {
            console.log('Monitor read error:', err);
          }
        });
      };
      reader.read().then(processChunk).catch((err) => {
        if (err.name !== 'NetworkError') {
          console.log('Monitor read error:', err);
        }
      });
    } catch (err) {
      this.store.dispatch(getNewMessage(`Error al abrir monitor: ${err.message || err}`));
    }
  }

  closeMonitor = async (port = '') => {
    if (this.sharedPort) {
      await this.closeSharedMonitor();
      return;
    }

    try {
      if (this.monitorReader) {
        await this.monitorReader.cancel();
        this.monitorReader.releaseLock();
        this.monitorReader = null;
      }
      if (this.monitorPort && this.monitorPort.readable) {
        await this.monitorPort.close();
      }
      this.monitorPort = null;
    } catch (err) {
      console.log('Close monitor error:', err);
    }
  }

  sendMonitor = async (port = '', command = '') => {
    const serialPort = this.getSerialPortByName(port);
    if (!serialPort) return;

    // Con el puerto compartido el writer es de la sesión: pedir otro acá chocaría
    // con el lock que ya tiene tomado.
    if (this.sharedPort) {
      const session = getSerialSession(serialPort);
      await session.write(command);
      return;
    }

    if (!serialPort.writable) return;

    try {
      const writer = serialPort.writable.getWriter();
      const encoder = new TextEncoder();
      await writer.write(encoder.encode(command));
      writer.releaseLock();
    } catch (err) {
      this.store.dispatch(getNewMessage(`Error al enviar comando: ${err.message || err}`));
    }
  }
}

export default WebESPAgent;
